import { Fragment, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { CheckboxInput } from "@/components/ui/Checkbox";
import { EmptyState, type EmptyStateAction } from "@/components/ui/EmptyState";
import { PencilIcon } from "@/components/ui/icons";
import { Select } from "@/components/ui/Select";
import { ConflictBanner } from "@/components/table/ConflictBanner";
import { FilterChip } from "@/components/table/FilterChip";
import { InlineEditCell } from "@/components/table/InlineEditCell";
import { TableSkeleton } from "@/components/table/TableSkeleton";
import { resolveColumnEdit, type DataTableColumn, type DataTableFilterConfig } from "@/components/table/columns";
import {
  type EditConflictResolution,
  type InlineEditCommitArgs,
  type InlineEditConflictArgs,
  useInlineEdits,
} from "@/components/table/use-inline-edits";
import type { Page } from "@/lib/api-types";
import { useIsReadOnly } from "@/lib/current-actor";
import { cx } from "@/lib/cx";
import { labelFor } from "@/lib/field-labels";
import { cellKey, nextEditableCell, previousEditableCell, type EditCommitOutcome } from "@/lib/inline-edit";
import {
  nextSortParam,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  PAGE_PARAM,
  SORT_PARAM,
} from "@/lib/list-query";
import { showToast } from "@/lib/toast-queue";
import { useIsPhone } from "@/lib/use-media-query";
import { useLoadingPhase } from "@/lib/use-loading-phase";

/**
 * The one table component every list screen reuses (R-W2, DESIGN.md
 * § Components "The shared table"). Configured by `columns` and nothing
 * else — no conditional branch anywhere in this file keys on a route path,
 * an entity name, or a specific list identity (VC-2.2-01, enforced by
 * `frontend/tests/architecture/data-table-generic.test.ts`'s source scan).
 *
 * Owns its own URL-derived sort/filter/page state (`useSearchParams`,
 * `lib/list-query.ts`'s pure grammar functions) and the click handlers that
 * write it back — every list screen gets sorting, filtering, paging,
 * loading and empty states "for free" just by passing `columns`, `page`
 * (the fetched envelope) and a few callbacks. The caller (a route) owns
 * fetching: building the query string from the same URL state via
 * `buildListSearch` and calling `apiFetch` through TanStack Query with
 * `placeholderData: keepPreviousData`, so paging/sorting never re-triggers
 * `isPending` (VC-2.2-17) — only the very first load of a screen does.
 */
/**
 * The "EXPLORATORY SPIKE" vibrant palette (`index.css`), mirroring
 * `StatTile`/`RecordPage`'s own identical `tint` prop shape exactly — this
 * table's own header tint is that same per-entity color identity, carried
 * from record page to list, so a table and the records inside it never
 * disagree about which color they are. Optional, defaulting to `"indigo"`
 * (the color the header tint already used before this prop existed), so a
 * list with no established record-page color of its own — Municipalities,
 * Reference lists, Import batches, Alerts — renders exactly as before,
 * never inventing a color identity these lists never established.
 */
export type DataTableTint = "indigo" | "cyan" | "violet" | "amber";

const DATA_TABLE_TINT_CLASSES: Record<DataTableTint, string> = {
  indigo: "bg-vibrant-indigo-quiet",
  cyan: "bg-vibrant-cyan-quiet",
  violet: "bg-vibrant-violet-quiet",
  amber: "bg-vibrant-amber-quiet",
};

export interface DataTableProps<Row extends { id: string }> {
  /** Names the list on its own, e.g. "Users" — the caption's base text. */
  caption: string;
  /** Plural noun for generic empty/filtered-empty sentences, e.g. "users". */
  itemNamePlural: string;
  /** This entity's own established color identity — see `DataTableTint`'s
   * own doc comment. Defaults to `"indigo"`. */
  tint?: DataTableTint;
  columns: DataTableColumn<Row>[];
  /** The fetched envelope, or `undefined` before any successful fetch has landed. */
  page: Page<Row> | undefined;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  filters?: DataTableFilterConfig[];
  /**
   * The unfiltered total, supplied by the caller once it has fetched one
   * (only needed when a filtered list comes back empty, VC-2.2-20) — never
   * fabricated here when the caller hasn't fetched it yet.
   */
  unfilteredTotal?: number;
  /**
   * Every row's own navigation target — omitted (a genuinely optional prop,
   * VC-2.2-01's own "one component, driven by config, never forked") for a
   * caller with no real per-row destination to send a click to (a wizard's
   * own preview step, showing not-yet-created rows nothing has an id for
   * yet): the identifier column then renders as plain text instead of a
   * `Link`, and a row click does nothing, rather than every caller needing
   * to invent a synthetic href that goes nowhere real.
   */
  getRowHref?: (row: Row) => string;
  /** Shown only when the list is empty with *no* filters active. */
  emptyState: { heading: string; body: string; action?: EmptyStateAction };
  /**
   * Inline editing (R-W3, Story 2.3). Present only when at least one column
   * carries an `edit` config; the caller (a route) owns the actual mutating
   * request, `expected_updated_at`, and the CSRF-aware `apiFetch` call —
   * this component only ever calls back into it, keeping `DataTable` itself
   * ignorant of any specific endpoint (the same rule VC-2.2-01 already
   * enforces for everything else in this file).
   */
  onCommitEdit?: (args: InlineEditCommitArgs<Row>) => Promise<EditCommitOutcome>;
  /** "Keep mine" / "Keep theirs" (judgement call 4's inline banner). Absent
   * only when no column carries `edit`. */
  onResolveConflict?: (args: InlineEditConflictArgs<Row>) => Promise<EditCommitOutcome>;
  /**
   * A trailing ghost row action — ghost/secondary only, never the red
   * destructive variant directly (DESIGN.md: "a destructive action in a
   * table row... is a ghost button labelled with its verb; the red button
   * is the second step, never the first" — the caller supplies a ghost
   * `Button` that opens its own `ConfirmDialog`, which is where red
   * appears). Desktop-only, hover-revealed (DESIGN.md § Component Patterns
   * "Data table row": "on phone they live on the record page, never
   * behind a swipe") — absent on the two-line phone row entirely, by
   * design, not by omission.
   */
  rowActions?: (row: Row) => ReactNode;
  /**
   * Row selection (Story 12.6, R-E10) — a checkbox column, present only
   * for a caller that needs a bulk operation across several rows at once.
   * Absent (the default, every prior caller) renders no checkbox column at
   * all, identical to before this was added. Desktop-only, mirroring
   * `rowActions`'s own identical "absent on the two-line phone row
   * entirely, by design, not by omission" choice above — a bulk operation
   * across many rows is a desktop workflow, and there is no room for a
   * third affordance beside the two-line phone row's own content.
   */
  selection?: {
    selectedIds: Set<string>;
    onSelectedIdsChange: (ids: Set<string>) => void;
    /**
     * A disabled (never merely omitted — spec judgement call: "the more
     * honest, less surprising choice") checkbox when this returns
     * `false` — e.g. an already-retired row a bulk edit may never target.
     * Every row is selectable when this is omitted entirely.
     */
    isRowSelectable?: (row: Row) => boolean;
  };
}

function assertPageShape<Row>(candidate: unknown): asserts candidate is Page<Row> {
  const DEFECT_MESSAGE =
    "DataTable received a response that is not the {data, meta} envelope AD-14 requires " +
    "— this is an API defect to fix at the route, not something the table silently handles.";
  if (Array.isArray(candidate)) {
    throw new Error(DEFECT_MESSAGE);
  }
  if (typeof candidate !== "object" || candidate === null) {
    throw new Error(DEFECT_MESSAGE);
  }
  const record = candidate as { data?: unknown; meta?: unknown };
  if (!Array.isArray(record.data) || typeof record.meta !== "object" || record.meta === null) {
    throw new Error(DEFECT_MESSAGE);
  }
}

export function DataTable<Row extends { id: string }>({
  caption,
  itemNamePlural,
  tint = "indigo",
  columns,
  page,
  isPending,
  isFetching,
  isError,
  errorMessage,
  filters,
  unfilteredTotal,
  getRowHref,
  emptyState,
  onCommitEdit,
  onResolveConflict,
  rowActions,
  selection,
}: DataTableProps<Row>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const loadingPhase = useLoadingPhase(isPending);
  const isReadOnly = useIsReadOnly();
  const [activeCell, setActiveCell] = useState<{ rowId: string; columnKey: string } | null>(null);
  const inlineEdits = useInlineEdits<Row>({
    rows: page?.data ?? [],
    columns,
    onCommit: onCommitEdit ?? (() => Promise.reject(new Error("no onCommitEdit configured"))),
    ...(onResolveConflict ? { onResolveConflict } : {}),
  });

  if (page !== undefined) {
    assertPageShape<Row>(page);
  }

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const filterFields = (filters ?? []).map((f) => f.field);
  const activeFilters = parseFilterParams(searchParams, filterFields);
  const currentPage = parsePageParam(searchParams.get(PAGE_PARAM));

  function updateParams(mutate: (params: URLSearchParams) => void) {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  }

  function handleSortClick(field: string) {
    updateParams((params) => {
      const next = nextSortParam(params.get(SORT_PARAM), field);
      if (next === null) {
        params.delete(SORT_PARAM);
      } else {
        params.set(SORT_PARAM, next);
      }
      params.delete(PAGE_PARAM);
    });
  }

  function handleFilterChange(field: string, value: string) {
    updateParams((params) => {
      if (value === "") {
        params.delete(field);
      } else {
        params.set(field, value);
      }
      params.delete(PAGE_PARAM);
    });
  }

  function clearFilter(field: string) {
    // Removing a filter can shrink the total row count below the page the
    // user was on (review finding: clearing from `?status=invited&page=2`
    // stranded the user on a stale `page=2` of a now-1-page result,
    // rendering the "genuinely empty" copy while real rows existed).
    updateParams((params) => {
      params.delete(field);
      params.delete(PAGE_PARAM);
    });
  }

  function goToPage(nextPage: number) {
    updateParams((params) => {
      if (nextPage <= 1) {
        params.delete(PAGE_PARAM);
      } else {
        params.set(PAGE_PARAM, String(nextPage));
      }
    });
  }

  // ------------------------------------------------------------------- //
  // Inline editing (R-W3, Story 2.3) — activation, commit, Esc, Tab.
  // Column-config-driven and generic over `Row`, the same "configured by a
  // column list and nothing else" rule the rest of this file already
  // follows (VC-2.2-01) — nothing below names a specific entity or route.
  // ------------------------------------------------------------------- //

  function rawCellValue(row: Row, column: DataTableColumn<Row>): string {
    const value = row[column.key];
    return value === null || value === undefined ? "" : String(value);
  }

  function activateCell(rowId: string, columnKey: string) {
    if (isReadOnly) {
      return;
    }
    setActiveCell({ rowId, columnKey });
  }

  function commitActiveCell(row: Row, column: DataTableColumn<Row>, value: string) {
    setActiveCell(null);
    // Nothing to save (review finding: Tab/Enter fired a PATCH even when
    // the value was untouched — a wasted write on every cell someone
    // merely tabbed through without changing).
    if (value === rawCellValue(row, column)) {
      return;
    }
    inlineEdits.commit(row, column, value);
  }

  /**
   * Story 15.5 — auto-save on navigate-away, `DataTable`'s own side of the
   * identical fix `RecordPage.tsx`'s `commitFieldOnLeave` documents in
   * full. `InlineEditCell` calls this from its own unmount cleanup in
   * place of `commitActiveCell` above when the cell being torn down was
   * active with a genuinely uncommitted `draft` — most often because its
   * row's own identifier `<Link>` was clicked, or the list page was
   * otherwise navigated away from, before the cell ever blurred. Same
   * write, same wire request as blur — the difference is purely how the
   * outcome is reported, since this row's own cell has nothing left on
   * screen to update by the time it resolves.
   */
  function commitCellOnLeave(row: Row, column: DataTableColumn<Row>, value: string) {
    if (value === rawCellValue(row, column)) {
      return;
    }
    void inlineEdits.commitAwaitable(row, column, value).then((outcome) => {
      if (outcome.ok) {
        showToast("Saved.");
      } else {
        showToast("Didn't save. Please make that change again.");
      }
    });
  }

  function tabFromActiveCell(row: Row, column: DataTableColumn<Row>, value: string) {
    setActiveCell(null);
    if (value !== rawCellValue(row, column)) {
      inlineEdits.commit(row, column, value);
    }
    const next = nextEditableCell(page?.data ?? [], columns, { rowId: row.id, columnKey: column.key });
    if (next !== null) {
      setActiveCell(next);
    }
  }

  function shiftTabFromActiveCell(row: Row, column: DataTableColumn<Row>, value: string) {
    setActiveCell(null);
    if (value !== rawCellValue(row, column)) {
      inlineEdits.commit(row, column, value);
    }
    const previous = previousEditableCell(page?.data ?? [], columns, { rowId: row.id, columnKey: column.key });
    if (previous !== null) {
      setActiveCell(previous);
    }
  }

  function resolveConflict(row: Row, column: DataTableColumn<Row>, resolution: EditConflictResolution) {
    void inlineEdits.resolveConflict(row, column, resolution);
  }

  // ------------------------------------------------------------------- //
  // Row selection (Story 12.6, R-E10) — desktop-only, see `selection`'s
  // own doc comment above for why.
  // ------------------------------------------------------------------- //

  function toggleRowSelected(row: Row) {
    if (!selection) return;
    const next = new Set(selection.selectedIds);
    if (next.has(row.id)) {
      next.delete(row.id);
    } else {
      next.add(row.id);
    }
    selection.onSelectedIdsChange(next);
  }

  function toggleSelectAllOnPage(rows: Row[]) {
    if (!selection) return;
    const selectableRows = rows.filter((row) => selection.isRowSelectable?.(row) ?? true);
    const allSelected = selectableRows.length > 0 && selectableRows.every((row) => selection.selectedIds.has(row.id));
    const next = new Set(selection.selectedIds);
    if (allSelected) {
      for (const row of selectableRows) next.delete(row.id);
    } else {
      for (const row of selectableRows) next.add(row.id);
    }
    selection.onSelectedIdsChange(next);
  }

  /**
   * Wraps a column's normal cell content in `InlineEditCell` when the
   * column carries an `edit` config and the account is not Read-only
   * (`useIsReadOnly`, the same withholding mechanism Story 2.1 established
   * — VC-2.3-17). Every other cell passes straight through unwrapped,
   * which is what keeps a read-only-configured column identical to today.
   */
  function editableCell(row: Row, column: DataTableColumn<Row>, resting: ReactNode): ReactNode {
    if (resolveColumnEdit(column, row) === undefined || isReadOnly) {
      return resting;
    }
    const key = cellKey(row.id, column.key);
    const cellIdentity = { rowId: row.id, columnKey: column.key };
    return (
      <InlineEditCell
        row={row}
        column={column}
        rawValue={rawCellValue(row, column)}
        isActive={activeCell?.rowId === row.id && activeCell.columnKey === column.key}
        onActivate={() => activateCell(row.id, column.key)}
        cellState={inlineEdits.cellStates[key]}
        onCommit={(value) => commitActiveCell(row, column, value)}
        onCommitOnLeave={(value) => commitCellOnLeave(row, column, value)}
        onCancel={() => setActiveCell(null)}
        onTab={(value) => tabFromActiveCell(row, column, value)}
        onShiftTab={(value) => shiftTabFromActiveCell(row, column, value)}
        onRetry={() => inlineEdits.retry(row, column)}
        hasNextEditableCell={nextEditableCell(page?.data ?? [], columns, cellIdentity) !== null}
        hasPreviousEditableCell={previousEditableCell(page?.data ?? [], columns, cellIdentity) !== null}
      >
        {resting}
      </InlineEditCell>
    );
  }

  /**
   * The identifier column (index 0) is `getRowHref`'s navigation target — a
   * real `<Link>` since Story 2.2, which making the whole cell a
   * click-to-edit trigger silently removed for every mouse and keyboard
   * user (review finding 1: `document.querySelectorAll('a[href]')` matching
   * the record page returned zero). Resting state keeps the real `<Link>`
   * plus a small, explicit edit affordance beside it — hover-revealed on
   * desktop (mouse-only concept, so it costs nothing there) and always
   * visible on phone, where hover does not exist. Once a cell here is
   * active or carries a pending write, it renders exactly like any other
   * editable cell (`editableCell`) — the Link/pencil pair is only the
   * *resting* rendering, never a second commit/keyboard mechanism.
   *
   * `href === undefined` (no `getRowHref` supplied at all, judgement call 4
   * above) renders plain, unlinked text instead of a `<Link>` — the
   * identifier column still shows the same value, it simply navigates
   * nowhere, since there is nowhere real for it to go.
   */
  function identifierCell(row: Row, column: DataTableColumn<Row>, href: string | undefined): ReactNode {
    const resting = renderCell(column, row);
    // `hover:underline` (live-caught missing): mirrors `RecentActivityRow.
    // tsx`'s own established row-identifier-link convention — Tailwind's
    // preflight strips the native anchor underline entirely, so with no
    // hover treatment this link carried zero visual affordance distinguishing
    // it from a secondary in-row link (e.g. TenantsList.tsx's own "Property"
    // column) that stays underlined at rest.
    const linked = href === undefined ? <span className="text-ink">{resting}</span> : (
      <Link to={href} className="text-ink no-underline hover:underline">{resting}</Link>
    );
    const editable = resolveColumnEdit(column, row) !== undefined && !isReadOnly;
    if (!editable) {
      return linked;
    }
    const key = cellKey(row.id, column.key);
    const isCellActive = activeCell?.rowId === row.id && activeCell.columnKey === column.key;
    const hasPendingState = inlineEdits.cellStates[key] !== undefined;
    if (isCellActive || hasPendingState) {
      return editableCell(row, column, resting);
    }
    return (
      <span className="group/identifier flex items-center gap-1">
        {linked}
        <button
          type="button"
          aria-label={`Edit ${labelFor(column.key)}`}
          onClick={(event) => {
            // The Link beside this button already navigates on its own
            // click, exactly as Story 2.2 built it — only this button's
            // click (activating an edit, not a navigation) must be kept
            // from also bubbling into `<tr onClick>`.
            event.stopPropagation();
            activateCell(row.id, column.key);
          }}
          className={cx(
            "inline-flex h-tap-target-min w-tap-target-min shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover",
            isPhone ? undefined : "opacity-0 focus:opacity-100 group-hover/identifier:opacity-100",
          )}
        >
          <PencilIcon />
        </button>
      </span>
    );
  }

  /** Any conflicted cell in this row, each rendered as its own banner row
   * immediately beneath (judgement call 4 — beside the row, never a modal). */
  function conflictBannersFor(row: Row, colSpan: number): ReactNode {
    const editableColumns = columns.filter((column) => resolveColumnEdit(column, row) !== undefined);
    return editableColumns.map((column) => {
      const state = inlineEdits.cellStates[cellKey(row.id, column.key)];
      if (state === undefined || state.status !== "conflict") {
        return null;
      }
      return (
        <ConflictBanner
          key={`${row.id}:${column.key}:conflict`}
          message={state.message ?? "Someone else changed this while it was being edited."}
          colSpan={colSpan}
          onKeepMine={() => resolveConflict(row, column, "mine")}
          onKeepTheirs={() => resolveConflict(row, column, "theirs")}
        />
      );
    });
  }

  const activeFilterEntries = Object.entries(activeFilters);
  const fullCaption =
    activeFilterEntries.length === 0
      ? caption
      : activeFilterEntries.length === 1
        ? `${caption} — filtered by ${(filters ?? []).find((f) => f.field === activeFilterEntries[0]?.[0])?.label ?? activeFilterEntries[0]?.[0]}`
        : `${caption} — filtered`;

  const filterRow =
    filters && filters.length > 0 ? (
      <div className="mb-4 flex flex-wrap items-end gap-4">
        {filters.map((filter) => (
          <label key={filter.field} className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{filter.label}</span>
            <Select
              value={activeFilters[filter.field] ?? ""}
              onChange={(event) => handleFilterChange(filter.field, event.target.value)}
            >
              <option value="">All</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </label>
        ))}
        {activeFilterEntries.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterEntries.map(([field, value]) => {
              const filterConfig = filters.find((f) => f.field === field);
              const optionLabel = filterConfig?.options.find((o) => o.value === value)?.label ?? value;
              return (
                <FilterChip
                  key={field}
                  label={`${filterConfig?.label ?? field}: ${optionLabel}`}
                  onRemove={() => clearFilter(field)}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    ) : null;

  if (isError) {
    return (
      <div>
        {filterRow}
        <div className="rounded-lg border border-border-hairline bg-surface p-6 text-body text-ink">
          <p>Couldn&apos;t load the {itemNamePlural} list.</p>
          {errorMessage ? <p className="mt-1 text-ink-secondary">{errorMessage}</p> : null}
        </div>
      </div>
    );
  }

  if (page === undefined) {
    // Nothing before 400ms; a skeleton between 400ms and 3s; the "unusual"
    // line after 3s. `loadingPhase` is "none" for the whole first stretch,
    // which means literally nothing renders here yet (VC-2.2-14).
    if (loadingPhase === "none") {
      return <div>{filterRow}</div>;
    }
    return (
      <div>
        {filterRow}
        {/* The vibrant palette's own card shadow (2026-08-27), replacing the
         * original plain hairline shell — see index.css's own comment on
         * `--shadow-card`/`--color-vibrant-*`, and DESIGN.md's Data table
         * bullet, both reconciled to this real system. */}
        <div className="overflow-hidden rounded-lg bg-surface shadow-card">
          <TableSkeleton columnCount={columns.length} isPhone={isPhone} />
        </div>
        {loadingPhase === "unusual" ? (
          <p className="mt-2 text-meta text-ink-secondary" role="status">
            Still loading. This is unusual at this size.
          </p>
        ) : null}
      </div>
    );
  }

  const rows = page.data;
  const totalPages = Math.max(1, Math.ceil(page.meta.total / Math.max(1, page.meta.page_size)));
  const hasActiveFilters = activeFilterEntries.length > 0;

  if (rows.length === 0) {
    if (hasActiveFilters) {
      const body =
        unfilteredTotal !== undefined
          ? `Clear the filters to see all ${unfilteredTotal} ${itemNamePlural}.`
          : `No ${itemNamePlural} match these filters.`;
      return (
        <div>
          {filterRow}
          <EmptyState
            heading={`No ${itemNamePlural} match these filters.`}
            body={body}
            action={{
              label: "Clear filters",
              onClick: () =>
                updateParams((params) => {
                  filterFields.forEach((field) => params.delete(field));
                  params.delete(PAGE_PARAM);
                }),
            }}
          />
        </div>
      );
    }
    return (
      <div>
        {filterRow}
        <EmptyState
          heading={emptyState.heading}
          body={emptyState.body}
          {...(emptyState.action ? { action: emptyState.action } : {})}
        />
      </div>
    );
  }

  const primaryColumn = columns[0];
  const secondaryColumns = columns.slice(1, 3);

  return (
    <div>
      {filterRow}
      {/*
       * The outer shell clips to `rounded.lg` corners; the inner layer is
       * the one true escape hatch if a column set is ever genuinely wider
       * than the space available (a future list with many columns) —
       * DESIGN.md bans horizontal scroll on the *page*, not necessarily on
       * a table's own content area, and AppShell's `min-w-0` (Story 2.2
       * review finding) only fixes the shell's ability to shrink, not a
       * table that's inherently too wide for any viewport. Scoping the
       * scroll to this div, not `<body>`, is what keeps the rail/header
       * fixed while only the dense grid pans.
       */}
      <div className="overflow-hidden rounded-lg border border-border-hairline bg-surface">
        <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <caption className="sr-only">{fullCaption}</caption>
          {isPhone ? null : (
          <thead>
            {/* EXPLORATORY SPIKE — tinted header, part of the same spike as
             * the card shadow just above. Owner-caught (2026-08-29): every
             * table used the same indigo regardless of entity, while each
             * entity's own record page already had a distinct color
             * identity (`tint` prop above) — carried through here so a
             * list and its own records never disagree about which color
             * they are. */}
            <tr className={cx("h-table-header border-b border-border-hairline", DATA_TABLE_TINT_CLASSES[tint])}>
              {selection ? (
                <th scope="col" className="w-tap-target-min px-4">
                  <CheckboxInput
                    aria-label={`Select all ${itemNamePlural}`}
                    checked={
                      rows.filter((row) => selection.isRowSelectable?.(row) ?? true).length > 0 &&
                      rows
                        .filter((row) => selection.isRowSelectable?.(row) ?? true)
                        .every((row) => selection.selectedIds.has(row.id))
                    }
                    onChange={() => toggleSelectAllOnPage(rows)}
                  />
                </th>
              ) : null}
              {columns.map((column) => {
                const isSortable = column.sortable !== false;
                const isActive = sort?.field === column.key;
                const isRightAligned = column.align === "right";
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(
                      // Owner-caught, 2026-08-28: darkened/bolded to match
                      // the "More details" card titles' identical fix —
                      // `font-medium`/`text-ink-secondary` read as too
                      // faint for a column header too.
                      "relative px-4 text-label font-semibold text-ink",
                      isRightAligned ? "text-right" : "text-left",
                    )}
                  >
                    {isSortable ? (
                      // The header row's own visible height is a fixed
                      // 40px (`h-table-header`, DESIGN.md's own token) — a
                      // button sized to its own content, or even to the
                      // full cell, tops out at 40px tall, short of the
                      // 44×44 accessibility floor. Absolutely positioning
                      // a 44px-tall (`h-tap-target-min`) button and
                      // centering it (`top-1/2 -translate-y-1/2`) gives it
                      // an invisible 2px hit-area overflow above and below
                      // the visible 40px row — the same "44×44 including
                      // the invisible hit area" pattern `icons.tsx`
                      // documents for icon-only nav buttons — without
                      // growing the row taller (position:absolute removes
                      // the button from flow entirely, so it cannot push
                      // `<tr>`'s own height).
                      <button
                        type="button"
                        onClick={() => handleSortClick(column.key)}
                        className={cx(
                          "absolute inset-x-0 top-1/2 flex h-tap-target-min -translate-y-1/2 items-center gap-1 px-4",
                          isRightAligned ? "flex-row-reverse justify-end" : "justify-start",
                        )}
                      >
                        {labelFor(column.key)}
                        {isActive ? (
                          <svg
                            width={12}
                            height={12}
                            viewBox="0 0 12 12"
                            aria-hidden="true"
                            className={sort?.descending ? "rotate-180" : undefined}
                          >
                            <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} />
                          </svg>
                        ) : null}
                      </button>
                    ) : (
                      labelFor(column.key)
                    )}
                  </th>
                );
              })}
              {rowActions ? (
                <th scope="col" className="w-tap-target-min px-4">
                  <span className="sr-only">Row actions</span>
                </th>
              ) : null}
            </tr>
          </thead>
          )}
          <tbody>
            {isPhone
              ? rows.map((row) => {
                  const href = getRowHref?.(row);
                  return (
                    <Fragment key={row.id}>
                      <tr
                        {...(href === undefined ? {} : { onClick: () => navigate(href) })}
                        className={cx(
                          "h-table-row-phone border-b border-border-hairline",
                          href === undefined ? undefined : "cursor-pointer hover:bg-surface-hover",
                        )}
                      >
                        <td className="px-4 py-2 align-middle">
                          <div className="flex flex-col justify-center gap-1">
                            {primaryColumn === undefined ? null : (
                              <span
                                className={cx(
                                  "text-body font-medium text-ink",
                                  primaryColumn.tabularFigures ? "numeric-features" : undefined,
                                  primaryColumn.align === "right" ? "text-right" : undefined,
                                )}
                              >
                                {identifierCell(row, primaryColumn, href)}
                              </span>
                            )}
                            {secondaryColumns.length > 0 ? (
                              <div className="flex flex-wrap gap-2 text-meta text-ink-secondary">
                                {secondaryColumns.map((column) => (
                                  <span
                                    key={column.key}
                                    className={cx(
                                      column.tabularFigures ? "numeric-features" : undefined,
                                      column.align === "right" ? "text-right" : undefined,
                                    )}
                                  >
                                    {editableCell(row, column, renderCell(column, row))}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {conflictBannersFor(row, 1)}
                    </Fragment>
                  );
                })
              : rows.map((row) => {
                  const href = getRowHref?.(row);
                  return (
                    <Fragment key={row.id}>
                      <tr
                        {...(href === undefined ? {} : { onClick: () => navigate(href) })}
                        className={cx(
                          "group h-table-row border-b border-border-hairline",
                          href === undefined ? undefined : "cursor-pointer hover:bg-surface-hover",
                        )}
                      >
                        {selection ? (
                          <td
                            className="px-4 align-middle"
                            onClick={(event) => {
                              // The row's own `onClick` navigates — a
                              // checkbox click must not also fire it
                              // (mirrors `rowActions`'s own identical
                              // `stopPropagation` a few lines below).
                              event.stopPropagation();
                            }}
                          >
                            <CheckboxInput
                              aria-label={`Select row ${row.id}`}
                              checked={selection.selectedIds.has(row.id)}
                              disabled={!(selection.isRowSelectable?.(row) ?? true)}
                              onChange={() => toggleRowSelected(row)}
                            />
                          </td>
                        ) : null}
                        {columns.map((column, index) => (
                          <td
                            key={column.key}
                            className={cx(
                              "px-4 align-middle",
                              column.align === "right" ? "text-right" : "text-left",
                              column.tabularFigures
                                ? "numeric-features text-numeric text-ink"
                                : "text-body text-ink",
                              index === 0 ? "font-medium" : undefined,
                            )}
                          >
                            {index === 0
                              ? identifierCell(row, column, href)
                              : editableCell(row, column, renderCell(column, row))}
                          </td>
                        ))}
                        {rowActions ? (
                          <td
                            className="px-4 align-middle"
                            onClick={(event) => {
                              // The row's own `onClick` navigates — a click
                              // inside this cell (opening a confirm dialog,
                              // say) must not also fire that navigation.
                              event.stopPropagation();
                            }}
                          >
                            <span className="flex justify-end opacity-0 focus-within:opacity-100 group-hover:opacity-100">
                              {rowActions(row)}
                            </span>
                          </td>
                        ) : null}
                      </tr>
                      {conflictBannersFor(row, columns.length + (rowActions ? 1 : 0) + (selection ? 1 : 0))}
                    </Fragment>
                  );
                })}
          </tbody>
        </table>
        </div>
      </div>
      {totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-meta text-ink-secondary">
            {rangeText(page.meta.page, page.meta.page_size, page.meta.total)}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-4 text-button text-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-4 text-button text-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
      {isFetching ? <span className="sr-only" role="status">Updating.</span> : null}
      {/*
       * The one shared inline-edit announcement (VC-2.3-05, DESIGN.md's
       * "Saved."/"Not saved. Retry available."), present from the first
       * render rather than mounted only once an edit happens — a live
       * region added to the DOM only at announcement time is exactly the
       * pattern screen readers are known to miss.
       */}
      <div aria-live="polite" className="sr-only">
        {inlineEdits.liveMessage}
      </div>
      {!inlineEdits.isOnline && Object.keys(inlineEdits.cellStates).length > 0 ? (
        <div
          role="status"
          className="fixed inset-x-0 bottom-0 z-10 border-t border-border-hairline bg-warning-quiet px-4 py-3 text-center text-body text-ink"
        >
          You&apos;re offline. Nothing is saving right now.
        </div>
      ) : null}
    </div>
  );
}

/**
 * A column/field's plain-language cell content: `column.render` if the
 * config supplies one, an em dash for an empty value, or the raw value
 * otherwise. Exported (Story 2.5) so the record-page shell's `FieldRow`
 * reuses the identical empty-value/em-dash rule (VC-2.5-09) rather than
 * re-deriving it — the same "configured by a column/field list" shape,
 * shared by its one real implementation.
 */
export function renderCell<Row>(column: DataTableColumn<Row>, row: Row) {
  if (column.render) {
    return column.render(row);
  }
  const value = row[column.key];
  return value === null || value === undefined ? "—" : String(value);
}

function rangeText(page: number, pageSize: number, total: number): string {
  if (total === 0) {
    return "0 of 0";
  }
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return `${start}–${end} of ${total}`;
}
